/* ============================================================================
 * Shared onboarding field config + edit rules
 * ----------------------------------------------------------------------------
 * Single source of truth for the column mapping, editable-field rules and the
 * ABN/TFN conversion guard, shared by OnboardingAllUsers, OnboardingUsersCards
 * and the OnboardingEditForm modal.
 *
 * FIELDS order MUST match ONBOARDING_ALL_FIELDS in the backend — index 0 is
 * the Airtable record id, then the field columns in the same order.
 *   idx 24 Working · idx 25 Master-Link · idx 26 WorkingDays
 * ==========================================================================*/

export const FIELDS = [
  { key: "recordId", label: null },
  { key: "validationIdConnect", label: "Validation ID" },
  { key: "snlType", label: "SNL Type" },
  { key: "email", label: "Email" },
  { key: "mobile", label: "Mobile" },
  { key: "fullName", label: "Full Name" },
  { key: "firstName", label: "First Name" },
  { key: "businessName", label: "Business Name" },
  { key: "typeOfEmployment", label: "Type Of Employment" },
  { key: "workStatus", label: "Work Status" },
  { key: "formComplete", label: "FormComplete" },
  { key: "employee", label: "Employee" },
  { key: "soletrader", label: "Soletrader" },
  { key: "declineTfn", label: "Decline_TFN" },
  { key: "partnership", label: "Partnership" },
  { key: "company", label: "Company" },
  { key: "position", label: "Position" },
  { key: "abnNumber", label: "ABN Number" },
  { key: "tfnNumber", label: "TFN Number" },
  { key: "weeklyHours", label: "Weekly Hours" },
  { key: "grossSalary", label: "Gross Salary" },
  { key: "superPayment", label: "Super Payment" },
  { key: "siteWorking", label: "Site Working" },
  { key: "startWork", label: "Start Work" },
  { key: "working", label: "Working" },        // idx 24 — lives on the MAIN table (via Master-Link)
  { key: "masterLink", label: "Master-Link" }, // idx 25 — main-table record id ("" on NU No Form)
  { key: "workingDays", label: "Working Days" }, // idx 26 — "Mon,Tues,…" or "None"
];

export const FIELD_KEYS = FIELDS.map((f) => f.key);

export const FIELD_LABELS = Object.fromEntries(
  FIELDS.filter((f) => f.label).map((f) => [f.key, f.label])
);

export const DATE_FIELDS = new Set(["startWork"]);

/* Fields saved by the Submit button. `working` is deliberately NOT here —
 * it saves on its own through /api/onboard-active/:id/working. */
export const EDITABLE_FIELDS = new Set([
  "email",
  "mobile",
  "firstName",
  "typeOfEmployment",
  "workStatus",
  "formComplete",
  "employee",
  "soletrader",
  "declineTfn",
  "partnership",
  "company",
  "position",
  "siteWorking",
  "weeklyHours",
  "grossSalary",
  "superPayment",
  "workingDays",
]);

export const NEW_COMPLETED_FIELDS = new Set([
  "formComplete",
  "employee",
  "soletrader",
  "declineTfn",
  "partnership",
  "company",
]);

export const NEW_COMPLETED_OPTIONS = ["New", "Completed"];
export const POSITION_OPTIONS = ["Full Time", "Part Time", "Casual"];
export const WORKING_OPTIONS = ["Working", "Not Working"];

/* the view whose records have no Master-Link yet — Working can't be set */
export const NO_MASTER_LINK_CATEGORY = "nyNoForm";

/* ---------- Working Days ----------
 * Stored in Airtable as a Mon→Sun ordered, comma-separated string
 * ("Mon,Tues,Fri") or "None". Same WEEKDAYS order as OnboardingForm. */
export const WEEKDAYS = ["Mon", "Tues", "Wed", "Thurs", "Fri", "Sat", "Sun"];
export const NO_WORKING_DAYS = "None";

const DAY_BY_LOWER = Object.fromEntries(WEEKDAYS.map((d) => [d.toLowerCase(), d]));

/* "Mon,Wed, fri" | "None" | "" -> ["Mon","Wed","Fri"]  (always Mon→Sun order) */
export const parseWorkingDays = (val) => {
  const picked = new Set(
    (val ?? "")
      .toString()
      .split(",")
      .map((s) => DAY_BY_LOWER[s.trim().toLowerCase()])
      .filter(Boolean)
  );
  return WEEKDAYS.filter((d) => picked.has(d));
};

/* ["Wed","Mon"] -> "Mon,Wed";  [] -> "None" */
export const formatWorkingDays = (days) => {
  const set = new Set(days ?? []);
  const ordered = WEEKDAYS.filter((d) => set.has(d));
  return ordered.length ? ordered.join(",") : NO_WORKING_DAYS;
};

/* Entity types grouped by the work status they belong to. */
export const ABN_TYPES = ["Sole Trader", "Company", "Partnership"];
export const TFN_TYPES = ["Employee"];

/* Type Of Employment options are constrained by the record's Work Status:
 *   ABN -> Sole Trader / Company / Partnership
 *   TFN -> Employee (only)
 * This makes an illegal ABN<->TFN conversion impossible to select. */
export const typeOptionsForStatus = (workStatus) => {
  if (workStatus === "ABN") return ABN_TYPES;
  if (workStatus === "TFN") return TFN_TYPES;
  return [...ABN_TYPES, ...TFN_TYPES];
};

/* `working` is last in every block so it sits beside Super Payment for
 * Employees and closes the block for everyone else. */
export const EMPLOYMENT_VISIBILITY = {
  Employee: ["formComplete", "employee", "weeklyHours", "grossSalary", "superPayment", "working"],
  "Sole Trader": ["formComplete", "soletrader", "declineTfn", "working"],
  Partnership: ["formComplete", "partnership", "declineTfn", "working"],
  Company: ["formComplete", "company", "working"],
};
export const CASUAL_EMPLOYEE_VISIBILITY = ["formComplete", "weeklyHours", "grossSalary", "superPayment", "working"];

export const TOP_FIELD_ORDER = [
  "fullName",
  "firstName",
  "email",
  "mobile",
  "businessName",
  "snlType",
  "typeOfEmployment",
  "workStatus",
  "position",
  "siteWorking",
  "startWork",
  "abnNumber",
  "tfnNumber",
  "workingDays",   // sits alongside TFN Number (spans the 2 remaining columns)
];

export const SUBMIT_KEYS = [
  "email",
  "mobile",
  "firstName",
  "typeOfEmployment",
  "workStatus",
  "position",
  "siteWorking",
  "formComplete",
  "employee",
  "soletrader",
  "declineTfn",
  "partnership",
  "company",
  "weeklyHours",
  "grossSalary",
  "superPayment",
  "workingDays",
];

/* ---------- helpers ---------- */

export const rowToRecord = (row) => {
  const record = {};
  FIELD_KEYS.forEach((key, i) => {
    record[key] = row[i] ?? "";
  });
  return record;
};

export const mobileDigits = (val) => (val ?? "").toString().replace(/\D/g, "");

export const formatMobile = (raw) => {
  const digits = mobileDigits(raw).slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const ABN_EMPLOYMENT_TYPES = new Set(ABN_TYPES);
const TFN_EMPLOYMENT_TYPES = new Set(TFN_TYPES);

export const deriveWorkStatus = (typeOfEmployment) => {
  if (ABN_EMPLOYMENT_TYPES.has(typeOfEmployment)) return "ABN";
  if (TFN_EMPLOYMENT_TYPES.has(typeOfEmployment)) return "TFN";
  return null;
};

/* ---------- ABN/TFN conversion guard ----------
 * Kept as a backend/edge safety net. With Work Status read-only and the Type
 * Of Employment dropdown constrained per status, an illegal flip can no longer
 * be selected in the UI — but this still protects against a bad payload. */
export const validateConversion = (originalStatus, intendedStatus, otherExists) => {
  if (intendedStatus === originalStatus) return { ok: true };

  if (originalStatus === "TFN" && intendedStatus === "ABN") {
    return {
      ok: false,
      message: otherExists
        ? "Can't convert TFN to ABN — an ABN record already exists for this user. Update the ABN record instead."
        : "Can't convert TFN to ABN. A new ABN record needs to be created from the Onboarding form.",
    };
  }

  if (originalStatus === "ABN" && intendedStatus === "TFN") {
    return {
      ok: false,
      message: otherExists
        ? "Can't convert ABN to TFN — a TFN record already exists for this user. Update the TFN record instead."
        : "Can't convert ABN to TFN. A new TFN record needs to be created from the Onboarding form.",
    };
  }

  return {
    ok: false,
    message: `Unsupported work status change from ${originalStatus} to ${intendedStatus}.`,
  };
};

export const validateSubmission = (payload, userRecords) => {
  const original = userRecords.find((r) => r.recordId === payload.recordId);
  if (!original) {
    return { ok: false, message: "Could not find the original record to validate against." };
  }

  // Working Days — at least one day must be selected; "None" can't be saved from the editor.
  if (parseWorkingDays(payload.workingDays).length === 0) {
    return { ok: false, message: "Select at least one working day — Working Days can't be None." };
  }

  const intendedStatus = deriveWorkStatus(payload.typeOfEmployment) || payload.workStatus;
  const otherExists = userRecords.some(
    (r) => r.recordId !== payload.recordId && r.workStatus === intendedStatus
  );
  return validateConversion(original.workStatus, intendedStatus, otherExists);
};